import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, Upload, Calendar, FileText, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import '../styles/LoginStyle.css';
import strings from '../../../utils/Strings';

interface AuthHeroProps {
  variant?: 'login' | 'register';
  hideCarousel?: boolean;
}

const AuthHeroComponent: React.FC<AuthHeroProps> = ({ variant = 'login', hideCarousel = false }) => {
  const items = [
    { title: strings.features[0].title, description: strings.features[0].description, icon: <Upload size={24} /> },
    { title: strings.features[1].title, description: strings.features[1].description, icon: <Calendar size={24} /> },
    { title: strings.features[2].title, description: strings.features[2].description, icon: <FileText size={24} /> },
    { title: strings.features[3].title, description: strings.features[3].description, icon: <Search size={24} /> },
  ];
  const [activeFeature, setActiveFeature] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % items.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [items.length]);

  return (
    <div className="login-hero">
      <div className="hero-bg-orb hero-bg-orb-1" />
      <div className="hero-bg-orb hero-bg-orb-2" />
      <div className="hero-bg-orb hero-bg-orb-3" />
      <div className="hero-grid-overlay" />

      <motion.div
        className="hero-inner"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="hero-top-row">
          <div className="hero-text-col">
            <motion.div
              className="hero-pill-badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Sparkles size={12} />
              <span>AI-Powered Hiring Platform</span>
            </motion.div>

            {variant === 'register' ? (
              <>
                <h1 className="hero-title">Register Your<br /><span className="hero-title-highlight">Enterprise Portal.</span></h1>
                <p className="hero-subtitle">Create your organization's admin account and unlock the full power of AI-driven hiring — candidate pipelines, smart scheduling, and real-time analytics.</p>
              </>
            ) : (
              <>
                <h1 className="hero-title">{strings.login.heroTitleLine1}<br /><span className="hero-title-highlight">{strings.login.heroTitleLine2}</span></h1>
                <p className="hero-subtitle">{strings.login.heroSubtitle}</p>
              </>
            )}

            <motion.a
              href="https://www.jozuna.com/contact_us.html"
              target="_blank"
              rel="noopener noreferrer"
              className="hero-cta-btn"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              Contact Us
              <ArrowRight size={16} />
            </motion.a>
          </div>
          <div className="hero-image-col">
            <img src="/iq_hero_blue.png" alt="Illustration" className="hero-illustration-img" />
          </div>
        </div>

        {!hideCarousel && (
          <>
            <div className="carousel-container">
              {items.map((item, index) => {
                let position = 'hidden';
                if (index === activeFeature) position = 'active';
                else if (index === (activeFeature - 1 + items.length) % items.length) position = 'prev';
                else if (index === (activeFeature + 1) % items.length) position = 'next';

                return (
                  <div key={index} className={`feature-card ${position}`}>
                    <div className="feature-icon-wrapper">
                      {item.icon}
                    </div>
                    <div className="feature-info">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="dot-indicators">
              {items.map((_, i) => (
                <div key={i} className={`dot ${i === activeFeature ? 'active' : ''}`} />
              ))}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AuthHeroComponent;
